import React, { useState, useEffect } from 'react';
import { Skill, SkillExperience } from '../types';

interface SkillSelectorProps {
  experiences: SkillExperience[];
  onChange: (experiences: SkillExperience[]) => void;
}

const SkillSelector: React.FC<SkillSelectorProps> = ({ experiences, onChange }) => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSkills, setFilteredSkills] = useState<Skill[]>([]);

  // 初期読み込み: スキル一覧（公開・件数付）
  useEffect(() => {
    fetchSkills();
  }, []);

  // 入力に応じてサジェストを更新（デバウンス + サーバー検索）
  useEffect(() => {
    const controller = new AbortController();
    const term = searchTerm.trim();

    const run = async () => {
      if (term.length === 0) {
        // デフォルトで候補を表示（件数順の上位10件）
        setFilteredSkills(
          [...skills]
            .sort((a, b) => (b.job_listing_total_count || 0) - (a.job_listing_total_count || 0))
            .slice(0, 10)
        );
        return;
      }

      try {
        const res = await fetch(`/api/v1/skills/search?keyword=${encodeURIComponent(term)}` , {
          headers: { 'Accept': 'application/json' },
          signal: controller.signal
        });
        if (!res.ok) throw new Error(`Failed to search skills: ${res.status}`);
        const data: Skill[] = await res.json();
        setFilteredSkills(data.slice(0, 10));
      } catch (e) {
        if ((e as any)?.name !== 'AbortError') {
          console.warn('Skill search failed', e);
        }
      }
    };

    const id = setTimeout(run, 200);
    return () => { clearTimeout(id); controller.abort(); };
  }, [searchTerm, skills]);

  const fetchSkills = async () => {
    try {
      const response = await fetch('/api/v1/skills', { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to load skills: ${response.status}`);
      const data: Skill[] = await response.json();
      // 件数降順で保持
      const sorted = [...data].sort((a, b) => (b.job_listing_total_count || 0) - (a.job_listing_total_count || 0));
      setSkills(sorted);
      // 初期候補（上位10件）
      setFilteredSkills(sorted.slice(0, 10));
    } catch (error) {
      console.error('スキル取得エラー:', error);
    }
  };

  const addSkill = (skill: Skill) => {
    if (!experiences.find(exp => exp.skill_id === skill.id)) {
      onChange([...experiences, { skill_id: skill.id, years_of_experience: 1 }]);
    }
    setSearchTerm('');
  };

  const removeSkill = (skillId: number) => {
    onChange(experiences.filter(exp => exp.skill_id !== skillId));
  };

  const updateYears = (skillId: number, years: number) => {
    onChange(experiences.map(exp => 
      exp.skill_id === skillId ? { ...exp, years_of_experience: years } : exp
    ));
  };

  const getSkillName = (skillId: number) => {
    return skills.find(skill => skill.id === skillId)?.name || '';
  };

  const termHasInput = (value: string) => value.trim().length > 0;

  return (
    <div>
      <div className="mb-3">
        <label className="form-label">スキルを検索・追加</label>
        <input
          type="text"
          className="form-control"
          placeholder="スキル名を入力..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />
        
        <div className="mt-2">
          {filteredSkills.length > 0 ? (
            <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filteredSkills.slice(0, 10).map(skill => (
                <button
                  key={skill.id}
                  type="button"
                  className="btn btn-outline-primary btn-sm me-2 mb-2"
                  onClick={() => addSkill(skill)}
                >
                  {skill.name}
                </button>
              ))}
            </div>
          ) : termHasInput(searchTerm) ? (
            <div className="text-muted small">該当するスキルが見つかりません</div>
          ) : null}
        </div>
      </div>

      <div>
        <label className="form-label">選択済みスキル</label>
        {experiences.length === 0 ? (
          <p className="text-muted">スキルが選択されていません</p>
        ) : (
          experiences.map(exp => (
            <div key={exp.skill_id} className="d-flex align-items-center mb-2">
              <span className="me-3 flex-grow-1">{getSkillName(exp.skill_id)}</span>
              <input
                type="number"
                className="form-control me-2"
                style={{ width: '100px' }}
                min="0"
                max="50"
                value={exp.years_of_experience}
                onChange={(e) => updateYears(exp.skill_id, parseInt(e.target.value) || 0)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
              <span className="me-2">年</span>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeSkill(exp.skill_id)}
              >
                削除
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SkillSelector;
